import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { GitBranch, Plus, Trash2, Pencil, Check, Search, Download, X, GripVertical, Settings, ArrowLeft, Layers, ThumbsDown, Route, ArrowRightLeft, Trophy, BadgeCheck, ShieldAlert, ListChecks, Columns2, Loader2, LayoutTemplate, Eye, EyeOff, Bold, Tag, Info, Smile, ImageOff, Copy, Timer, Brain, Zap, Mail, MessageCircle, FileText, Maximize2 } from "lucide-react";
import { ICON_OPTIONS } from "../lib/iconList";
import * as LucideIconSet from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "../components/ui/sonner";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLOR_SCALES = [
  { label: "Vermelho → Amarelo", colors: ["#DC2626", "#E03B20", "#E4501A", "#E86514", "#EC7A0E", "#F08F08", "#F4A402", "#F59E0B"] },
  { label: "Azul", colors: ["#BFDBFE", "#93C5FD", "#60A5FA", "#3B82F6", "#2563EB", "#1D4ED8", "#1E40AF", "#1E3A8A"] },
  { label: "Verde", colors: ["#A7F3D0", "#6EE7B7", "#34D399", "#10B981", "#059669", "#047857", "#065F46", "#064E3B"] },
  { label: "Vermelho", colors: ["#FECACA", "#FCA5A5", "#F87171", "#EF4444", "#DC2626", "#B91C1C", "#991B1B", "#7F1D1D"] },
  { label: "Cinza", colors: ["#E2E8F0", "#CBD5E1", "#94A3B8", "#64748B", "#475569", "#334155", "#1E293B", "#0F172A"] },
];

const SETTINGS_LIST = [
  { key: "passagem_obrigatoria", label: "Passagem por todos os estágios obrigatória", desc: "Todo card deve começar na primeira etapa e passar por todas." },
  { key: "proibido_voltar", label: "Proibido voltar de etapa", desc: "Um card nunca poderá ser levado para uma etapa anterior." },
  { key: "ganhar_ultima_etapa", label: "Ganhar card apenas na última etapa", desc: "Um card só pode ser ganho na última etapa do funil." },
  { key: "pode_ganhar", label: "Pode ganhar cards neste pipe", desc: "Indica se cards podem ser ganhos neste pipe." },
  { key: "pode_perder", label: "Pode perder cards neste pipe", desc: "Indica se cards podem ser perdidos neste pipe." },
];

// Build comprehensive icon options dynamically from all lucide-react icons
// Only include proper forwardRef components (what lucide icons are)
const _FORWARD_REF_SYM = Symbol.for("react.forward_ref");
const CARD_ICON_OPTIONS = [
  ...ICON_OPTIONS,
  // Add remaining lucide icons not in curated list
  ...Object.entries(LucideIconSet)
    .filter(([name, comp]) => /^[A-Z][a-zA-Z0-9]+$/.test(name) && !name.endsWith("Icon") && typeof comp?.render === "function" && !ICON_OPTIONS.find(o => o.name === name))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, Icon]) => ({ name, Icon, label: name.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2") })),
];

function AutentiqueTemplateSelector({ value, onChange }) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    axios.get(`${API}/autentique/templates`).then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);
  return (
    <div>
      <Label className="text-xs text-slate-500">Modelo de contrato</Label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full mt-1 border border-slate-200 rounded-lg p-2 text-sm text-slate-700 focus:outline-none">
        <option value="">Selecione um modelo...</option>
        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      {templates.length === 0 && <p className="text-[10px] text-slate-400 mt-1">Nenhum modelo criado. Vá em Integrações → Autentique → Contratos.</p>}
    </div>
  );
}

function PipelineSettingsForm({ pipeline, tc500, onSaved }) {
  const [localSettings, setLocalSettings] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (pipeline) {
      const s = {};
      SETTINGS_LIST.forEach(item => { s[item.key] = !!pipeline[item.key]; });
      setLocalSettings(s);
    }
  }, [pipeline]);
  const hasChanges = SETTINGS_LIST.some(s => localSettings[s.key] !== !!pipeline?.[s.key]);
  return (
    <>
      <div className="space-y-3">
        {SETTINGS_LIST.map(s => (
          <div key={s.key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
            <div className="flex-1 mr-4"><p className="text-sm font-medium text-slate-800">{s.label}</p><p className="text-xs text-slate-400 mt-0.5">{s.desc}</p></div>
            <Switch checked={!!localSettings[s.key]} onCheckedChange={v => setLocalSettings(prev => ({ ...prev, [s.key]: v }))} />
          </div>
        ))}
      </div>
      <Button
        disabled={!hasChanges || saving}
        onClick={async () => {
          setSaving(true);
          try {
            await axios.put(`${API}/pipelines/${pipeline.id}`, localSettings, { withCredentials: true });
            toast.success("Configurações salvas!");
            onSaved(localSettings);
          } catch { toast.error("Erro ao salvar"); }
          setSaving(false);
        }}
        className="w-full text-white hover:opacity-90 mt-1"
        style={{ backgroundColor: tc500 }}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Salvar
      </Button>
    </>
  );
}



export default function PipelinePage() {
  const { t } = useTranslation();
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [stages, setStages] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pipelineDialogOpen, setPipelineDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [motivosDialogOpen, setMotivosDialogOpen] = useState(false);
  const [winFlowDialogOpen, setWinFlowDialogOpen] = useState(false);
  const [winFlowConfig, setWinFlowConfig] = useState(null);
  const [winFlowTab, setWinFlowTab] = useState("docs");
  const [promptFullscreen, setPromptFullscreen] = useState(false);

  useEffect(() => {
    if (winFlowDialogOpen && selectedPipeline) {
      setWinFlowConfig(null);
      axios.get(`${API}/pipelines/${selectedPipeline.id}/win-flow`).then(r => setWinFlowConfig(r.data)).catch(() => setWinFlowConfig({
        pipeline_id: selectedPipeline.id,
        required_documents: [],
        ai_validation_enabled: false,
        ai_prompt: "",
        post_win_email_enabled: false,
        post_win_email_template_id: "",
        post_win_whatsapp_enabled: false,
        post_win_whatsapp_message: "",
      }));
    }
  }, [winFlowDialogOpen, selectedPipeline]);
  const [editPipeline, setEditPipeline] = useState(null);
  const [editStage, setEditStage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [pipelineForm, setPipelineForm] = useState({ name: "" });
  const [stageForm, setStageForm] = useState({ name: "", color: "#3B82F6" });
  const [newMotivo, setNewMotivo] = useState("");
  const [editMotivo, setEditMotivo] = useState(null);
  const [editMotivoName, setEditMotivoName] = useState("");
  const [stageFieldsDialogOpen, setStageFieldsDialogOpen] = useState(false);
  const [stageFieldsTarget, setStageFieldsTarget] = useState(null);
  const [stageFieldsList, setStageFieldsList] = useState([]);
  const [allCustomFields, setAllCustomFields] = useState([]);
  const [allStageFields, setAllStageFields] = useState({});
  const [cardLayoutDialogOpen, setCardLayoutDialogOpen] = useState(false);
  const [cardLayoutTarget, setCardLayoutTarget] = useState(null);
  const [cardLayoutFields, setCardLayoutFields] = useState([]);
  const [openIconPicker, setOpenIconPicker] = useState(null);
  const [iconSearchQuery, setIconSearchQuery] = useState("");
  const [applyToAllOpen, setApplyToAllOpen] = useState(false);
  const [duplicatePipelineTarget, setDuplicatePipelineTarget] = useState(null);
  const [duplicateStageTarget, setDuplicateStageTarget] = useState(null);
  const [slaDialogOpen, setSlaDialogOpen] = useState(false);
  const [slaTarget, setSlaTarget] = useState(null);
  const [slaForm, setSlaForm] = useState({ prazo_dias: "", prazo_inatividade_dias: "" });
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [reorderPending, setReorderPending] = useState(null);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#06B6D4";
  const tc50 = appearance.colors?.["50"] || "#ECFEFF";

  const fetchPipelines = useCallback(async () => {
    try { const res = await axios.get(`${API}/pipelines`, { withCredentials: true }); setPipelines(res.data); } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  const fetchStages = useCallback(async (pid) => {
    setDetailLoading(true);
    try { const res = await axios.get(`${API}/pipeline-stages?pipeline_id=${pid}`, { withCredentials: true }); setStages(res.data); } catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  }, []);

  const fetchMotivos = useCallback(async (pid) => {
    try { const res = await axios.get(`${API}/pipelines/${pid}/motivos-perda`, { withCredentials: true }); setMotivos(res.data); } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchPipelines(); }, [fetchPipelines]);
  useEffect(() => { if (selectedPipeline) { fetchStages(selectedPipeline.id); fetchMotivos(selectedPipeline.id); } }, [selectedPipeline, fetchStages, fetchMotivos]);

  const fetchCustomFields = useCallback(async () => {
    try { const res = await axios.get(`${API}/custom-fields?module=negocios`, { withCredentials: true }); setAllCustomFields(res.data); } catch (err) { console.error(err); }
  }, []);
  useEffect(() => { fetchCustomFields(); }, [fetchCustomFields]);

  // Fetch all stage fields when stages change
  useEffect(() => {
    const fetchAllStageFields = async () => {
      const map = {};
      for (const s of stages) {
        try { const res = await axios.get(`${API}/pipeline-stages/${s.id}/fields`, { withCredentials: true }); map[s.id] = res.data.required_fields || []; } catch { map[s.id] = []; }
      }
      setAllStageFields(map);
    };
    if (stages.length > 0) fetchAllStageFields();
  }, [stages]);

  const openStageFields = async (stage) => {
    setStageFieldsTarget(stage);
    try { const res = await axios.get(`${API}/pipeline-stages/${stage.id}/fields`, { withCredentials: true }); setStageFieldsList(res.data.required_fields || []); } catch (err) { setStageFieldsList([]); }
    setStageFieldsDialogOpen(true);
  };
  const toggleStageField = (fieldName) => {
    setStageFieldsList(prev => prev.includes(fieldName) ? prev.filter(f => f !== fieldName) : [...prev, fieldName]);
  };
  const saveStageFields = async () => {
    if (!stageFieldsTarget) return;
    try { await axios.put(`${API}/pipeline-stages/${stageFieldsTarget.id}/fields`, { required_fields: stageFieldsList }, { withCredentials: true }); } catch (err) { console.error(err); }
    setAllStageFields(prev => ({ ...prev, [stageFieldsTarget.id]: stageFieldsList }));
    setStageFieldsDialogOpen(false);
  };

  const buildDefaultCardFields = () => {
    return allCustomFields.map((cf, i) => ({
      field_name: cf.field_name, label: cf.field_label, fixed: false,
      visible: false, bold: false, badge: false, badge_color: "#3B82F6", icon: null, order: i,
    }));
  };

  const openCardLayout = async (stage) => {
    setCardLayoutTarget(stage);
    setOpenIconPicker(null);
    setIconSearchQuery("");
    try {
      const [layoutRes, cardRes, modulesRes] = await Promise.all([
        axios.get(`${API}/form-layout/negocios`, { withCredentials: true }),
        axios.get(`${API}/pipeline-stages/${stage.id}/card-layout`, { withCredentials: true }),
        axios.get(`${API}/modules`, { withCredentials: true }),
      ]);
      const savedFields = cardRes.data.fields || [];
      const formFields = layoutRes.data.fields || [];
      const formSections = layoutRes.data.sections || [];
      // Build module enabled map
      const modMap = {};
      (modulesRes.data || []).forEach(m => { modMap[m.module] = m.enabled; });
      const fieldToModule = { empresa: "empresas", contato: "contatos", produto: "produtos" };
      // Collect all fields from layout (main + sections), deduplicated
      const seen = new Set();
      const allLayoutFields = [];
      for (const f of [...formFields, ...formSections.flatMap(s => s.fields || [])]) {
        if (!seen.has(f.field_name)) { seen.add(f.field_name); allLayoutFields.push(f); }
      }
      const merged = allLayoutFields.map((f, i) => {
        const label = f.field_label || f.label || f.field_name;
        // Use module enabled instead of required_field for empresa/contato/produto
        const moduleKey = fieldToModule[f.field_name];
        const isLayoutDisabled = moduleKey ? (modMap[moduleKey] === false) : false;
        const saved = !isLayoutDisabled ? savedFields.find(s => s.field_name === f.field_name) : null;
        const defaults = {
          ...f, label,
          visible: isLayoutDisabled ? false : ["titulo", "valor_mrr"].includes(f.field_name),
          bold: f.field_name === "titulo",
          badge: false, badge_color: "#3B82F6", icon: null, order: i,
          layout_disabled: isLayoutDisabled,
        };
        if (saved) return { ...defaults, ...saved, layout_disabled: false };
        return defaults;
      });
      // Add native fields at the end if not already present
      const tipo = appearance.tipo_negocio_padrao || "recorrente_setup";
      const nativeEntries = tipo === "recorrente_setup"
        ? [{ field_name: "valor_setup", label: "Setup" }, { field_name: "valor_mrr", label: "MRR" }]
        : [{ field_name: "valor_negocio", label: "Valor do Negócio" }];
      // Always include all three for safety
      const allNatives = [
        { field_name: "valor_setup", label: "Setup" },
        { field_name: "valor_mrr", label: "MRR" },
        { field_name: "valor_negocio", label: "Valor do Negócio" },
      ];
      for (const nf of allNatives) {
        if (!merged.find(m => m.field_name === nf.field_name)) {
          const saved = savedFields.find(s => s.field_name === nf.field_name);
          merged.push({ field_name: nf.field_name, label: nf.label, visible: saved?.visible ?? false, bold: saved?.bold ?? false, badge: saved?.badge ?? false, badge_color: saved?.badge_color || "#3B82F6", icon: saved?.icon || null, order: merged.length, layout_disabled: false, ...(saved || {}) });
        }
      }
      setCardLayoutFields(merged);
    } catch (err) {
      console.error("[CardLayout] error:", err);
      setCardLayoutFields([]);
    }
    setCardLayoutDialogOpen(true);
  };

  const updateCardField = (fieldName, key, value) => {
    setCardLayoutFields(prev => prev.map(f => f.field_name === fieldName ? { ...f, [key]: value } : f));
  };

  const saveCardLayout = async () => {
    if (!cardLayoutTarget) return;
    try { await axios.put(`${API}/pipeline-stages/${cardLayoutTarget.id}/card-layout`, { fields: cardLayoutFields }, { withCredentials: true }); } catch (err) { console.error(err); }
    setCardLayoutDialogOpen(false);
  };

  const applyToAllStages = async () => {
    setApplyToAllOpen(false);
    await Promise.all(stages.map(s =>
      axios.put(`${API}/pipeline-stages/${s.id}/card-layout`, { fields: cardLayoutFields }, { withCredentials: true }).catch(err => console.error(err))
    ));
    setCardLayoutDialogOpen(false);
  };

  const confirmDuplicatePipeline = async () => {
    if (!duplicatePipelineTarget) return;
    try { await axios.post(`${API}/pipelines/${duplicatePipelineTarget.id}/duplicate`, {}, { withCredentials: true }); fetchPipelines(); } catch (err) { console.error(err); }
    setDuplicatePipelineTarget(null);
  };

  const confirmDuplicateStage = async () => {
    if (!duplicateStageTarget) return;
    try { await axios.post(`${API}/pipeline-stages/${duplicateStageTarget.id}/duplicate`, {}, { withCredentials: true }); fetchStages(selectedPipeline.id); } catch (err) { console.error(err); }
    setDuplicateStageTarget(null);
  };

  const openSla = async (stage) => {
    setSlaTarget(stage);
    try {
      const res = await axios.get(`${API}/pipeline-stages/${stage.id}/sla`, { withCredentials: true });
      setSlaForm({ prazo_dias: res.data.prazo_dias ?? "", prazo_inatividade_dias: res.data.prazo_inatividade_dias ?? "" });
    } catch { setSlaForm({ prazo_dias: "", prazo_inatividade_dias: "" }); }
    setSlaDialogOpen(true);
  };

  const saveSla = async () => {
    if (!slaTarget) return;
    const payload = {
      prazo_dias: slaForm.prazo_dias !== "" ? parseInt(slaForm.prazo_dias) : null,
      prazo_inatividade_dias: slaForm.prazo_inatividade_dias !== "" ? parseInt(slaForm.prazo_inatividade_dias) : null,
    };
    try { await axios.put(`${API}/pipeline-stages/${slaTarget.id}/sla`, payload, { withCredentials: true }); } catch (err) { console.error(err); }
    setSlaDialogOpen(false);
  };

  // Pipeline CRUD
  const openNewPipeline = () => { setEditPipeline(null); setPipelineForm({ name: "" }); setPipelineDialogOpen(true); };
  const openEditPipeline = (p) => { setEditPipeline(p); setPipelineForm({ name: p.name }); setPipelineDialogOpen(true); };
  const submitPipeline = async (e) => {
    e.preventDefault();
    try {
      if (editPipeline) await axios.put(`${API}/pipelines/${editPipeline.id}`, { name: pipelineForm.name }, { withCredentials: true });
      else await axios.post(`${API}/pipelines`, { name: pipelineForm.name }, { withCredentials: true });
      setPipelineDialogOpen(false); fetchPipelines();
    } catch (err) { console.error(err); }
  };

  // Stage CRUD
  const openNewStage = () => { setEditStage(null); setStageForm({ name: "", color: "#3B82F6" }); setStageDialogOpen(true); };
  const openEditStage = (s) => { setEditStage(s); setStageForm({ name: s.name, color: s.color }); setStageDialogOpen(true); };
  const submitStage = async (e) => {
    e.preventDefault();
    try {
      if (editStage) await axios.put(`${API}/pipeline-stages/${editStage.id}`, stageForm, { withCredentials: true });
      else await axios.post(`${API}/pipeline-stages`, { ...stageForm, pipeline_id: selectedPipeline.id }, { withCredentials: true });
      setStageDialogOpen(false); fetchStages(selectedPipeline.id);
    } catch (err) { console.error(err); }
  };

  // Motivos CRUD
  const addMotivo = async () => {
    if (!newMotivo.trim()) return;
    try { await axios.post(`${API}/pipelines/${selectedPipeline.id}/motivos-perda`, { nome: newMotivo }, { withCredentials: true }); setNewMotivo(""); fetchMotivos(selectedPipeline.id); } catch (err) { console.error(err); }
  };
  const saveEditMotivo = async () => {
    if (!editMotivo || !editMotivoName.trim()) return;
    try { await axios.put(`${API}/pipelines/${selectedPipeline.id}/motivos-perda/${editMotivo.id}`, { nome: editMotivoName }, { withCredentials: true }); setEditMotivo(null); fetchMotivos(selectedPipeline.id); } catch (err) { console.error(err); }
  };
  const deleteMotivo = async (id) => {
    try { await axios.delete(`${API}/pipelines/${selectedPipeline.id}/motivos-perda/${id}`, { withCredentials: true }); fetchMotivos(selectedPipeline.id); } catch (err) { console.error(err); }
  };

  // Delete
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteType === "pipeline") { await axios.delete(`${API}/pipelines/${deleteTarget.id}`, { withCredentials: true }); if (selectedPipeline?.id === deleteTarget.id) setSelectedPipeline(null); fetchPipelines(); }
      else { await axios.delete(`${API}/pipeline-stages/${deleteTarget.id}`, { withCredentials: true }); fetchStages(selectedPipeline.id); }
    } catch (err) { console.error(err); }
    setDeleteTarget(null); setDeleteType("");
  };

  // Drag reorder
  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) { dragItem.current = null; dragOverItem.current = null; return; }
    const fromIdx = dragItem.current;
    const toIdx = dragOverItem.current;
    const items = [...stages]; const [dragged] = items.splice(fromIdx, 1); items.splice(toIdx, 0, dragged);
    dragItem.current = null; dragOverItem.current = null;
    setReorderPending({ stageName: dragged.name, newPosition: toIdx + 1, items });
  };
  const confirmReorder = async () => {
    if (!reorderPending) return;
    setStages(reorderPending.items);
    try { await axios.put(`${API}/pipeline-stages-reorder`, reorderPending.items.map(s => ({ id: s.id })), { withCredentials: true }); } catch (err) { console.error(err); }
    setReorderPending(null);
  };
  const cancelReorder = () => { setReorderPending(null); };

  // Toggle setting
  const toggleSetting = async (key) => {
    const newVal = !selectedPipeline[key];
    setSelectedPipeline(prev => ({ ...prev, [key]: newVal }));
    try { await axios.put(`${API}/pipelines/${selectedPipeline.id}`, { [key]: newVal }, { withCredentials: true }); } catch (err) { console.error(err); }
  };

  const handleExport = () => {
    const rows = stages.map((s, i) => ({ "Etapa": s.name, "Ordem": i + 1, "Cor": s.color }));
    const ws = XLSX.utils.json_to_sheet(rows); ws["!cols"] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 15) }));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Pipeline");
    XLSX.writeFile(wb, `pipeline_${selectedPipeline?.name || "export"}_${new Date().toISOString().slice(0, 10)}.xlsx`); setExportDialogOpen(false);
  };

  const filteredPipelines = pipelines.filter(p => !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredStages = stages.filter(s => !searchTerm || s.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  // =================== PIPELINE DETAIL VIEW ===================
  if (selectedPipeline) {
    if (detailLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: tc500 }} />
          <p className="text-sm">{t("ui.carregando_pipeline")}</p>
        </div>
      );
    }
    return (
      <div data-testid="pipeline-detail-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="icon" onClick={() => { setSelectedPipeline(null); setSearchTerm(""); }} className="text-slate-400 hover:text-slate-600 h-10 w-10"><ArrowLeft className="w-5 h-5" /></Button></TooltipTrigger><TooltipContent>{t("ui.voltar")}</TooltipContent></Tooltip>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}><GitBranch className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} /></div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">{selectedPipeline.name} <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{filteredStages.length}</Badge></h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar etapa..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
            </div>
            <Tooltip><TooltipTrigger asChild><Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setExportDialogOpen(true)}><Download className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("ui.exportar")}</TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><Button onClick={openNewStage} size="icon" style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90 h-9 w-9"><Plus className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("configuracoes.pipeline.newStage")}</TooltipContent></Tooltip>
          </div>
        </div>

        {/* Pipeline Action Buttons */}
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2 text-sm bg-white" onClick={() => setSettingsDialogOpen(true)}>
            <Settings className="w-4 h-4" />Config. Gerais
          </Button>
          <Button variant="outline" className="gap-2 text-sm bg-white" onClick={() => setMotivosDialogOpen(true)}>
            <ThumbsDown className="w-4 h-4" />Motivos de Perda
          </Button>
          <Button variant="outline" className="gap-2 text-sm bg-white" onClick={() => setWinFlowDialogOpen(true)}>
            <Trophy className="w-4 h-4" />Fluxo de Ganho
          </Button>
        </div>

        {/* Draggable Stages */}
        <div className="space-y-3">
          {filteredStages.map((stage, i) => (
            <div key={stage.id} draggable onDragStart={() => { dragItem.current = i; }} onDragEnter={() => { dragOverItem.current = i; }} onDragEnd={handleDragEnd} onDragOver={e => e.preventDefault()}
              className="bg-white rounded-lg border border-slate-200 shadow-sm hover:border-slate-400 transition-colors p-4 flex items-center gap-4 cursor-pointer" onClick={() => openEditStage(stage)}>
              <div className="text-slate-300 cursor-grab active:cursor-grabbing flex-shrink-0" onClick={e => e.stopPropagation()}><GripVertical className="w-4 h-4" /></div>
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm" style={{ backgroundColor: stage.color }}>{i + 1}</div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-slate-900">{stage.name}</span>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <div className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-500 text-[11px]">Etapa {i + 1}</div>
                  {i === 0 && <Tooltip><TooltipTrigger asChild><div className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-500 text-[11px]">Novo</div></TooltipTrigger><TooltipContent>Novos negócios criados no formulário exibirão os campos configurados nesta etapa</TooltipContent></Tooltip>}
                  {(allStageFields[stage.id] || []).map(fn => {
                    const NATIVE_LABELS = { "__valor_setup": "Setup", "__valor_mrr": "MRR", "__valor_negocio": "Valor do Negócio" };
                    if (NATIVE_LABELS[fn]) return <div key={fn} className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-500 text-[11px]">{NATIVE_LABELS[fn]}</div>;
                    const cf = allCustomFields.find(c => c.field_name === fn);
                    return cf ? <div key={fn} className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-500 text-[11px]">{cf.field_label}</div> : null;
                  })}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); openSla(stage); }} className="text-slate-500 hover:text-amber-600 hover:bg-amber-50"><Timer className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Prazo da etapa</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); openStageFields(stage); }} className="text-slate-500 hover:text-sky-600 hover:bg-sky-50"><ListChecks className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("ui.campos_obrigatorios_desta_etapa")}</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); openCardLayout(stage); }} className="text-slate-500 hover:text-violet-600 hover:bg-violet-50"><LayoutTemplate className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Configurar Cards Kanban</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); setDuplicateStageTarget(stage); }} className="text-slate-500 hover:text-teal-600 hover:bg-teal-50"><Copy className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Duplicar etapa</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); openEditStage(stage); }} className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"><Pencil className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("common.edit")}</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); setDeleteTarget(stage); setDeleteType("stage"); }} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("common.delete")}</TooltipContent></Tooltip>
              </div>
            </div>
          ))}
        </div>

        {/* Settings Dialog */}
        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Settings className="w-5 h-5" style={{ color: tc500 }} />Configurações do Pipeline</DialogTitle></DialogHeader>
            <PipelineSettingsForm pipeline={selectedPipeline} tc500={tc500} onSaved={(updated) => { setSelectedPipeline(prev => ({ ...prev, ...updated })); setSettingsDialogOpen(false); }} />
          </DialogContent>
        </Dialog>

        {/* Motivos de Perda Dialog */}
        <Dialog open={motivosDialogOpen} onOpenChange={setMotivosDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ThumbsDown className="w-5 h-5" style={{ color: tc500 }} />{t("ui.motivos_de_perda")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input value={newMotivo} onChange={e => setNewMotivo(e.target.value)} placeholder="Novo motivo de perda..." className="flex-1" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addMotivo())} />
                <Button onClick={addMotivo} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90 px-3"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {motivos.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-400"><ThumbsDown className="w-8 h-8 mb-2 text-slate-300" /><p className="text-sm">Nenhum motivo cadastrado.</p></div>
                ) : motivos.map(m => (
                  <div key={m.id} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2.5">
                    {editMotivo?.id === m.id ? (
                      <>
                        <Input value={editMotivoName} onChange={e => setEditMotivoName(e.target.value)} className="flex-1 h-8 text-sm" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), saveEditMotivo())} />
                        <Button variant="ghost" size="sm" onClick={saveEditMotivo} className="text-emerald-600 hover:text-emerald-700 h-7 w-7 p-0"><Check className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditMotivo(null)} className="text-slate-400 hover:text-slate-600 h-7 w-7 p-0"><X className="w-3.5 h-3.5" /></Button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-slate-700 flex-1">{m.nome}</span>
                        <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => { setEditMotivo(m); setEditMotivoName(m.nome); }} className="text-sky-600 hover:text-sky-700 h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent>{t("common.edit")}</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => deleteMotivo(m.id)} className="text-rose-500 hover:text-rose-600 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent>{t("common.delete")}</TooltipContent></Tooltip>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Win Flow Config Dialog */}
        <Dialog open={winFlowDialogOpen} onOpenChange={setWinFlowDialogOpen}>
          <DialogContent className="max-w-2xl !p-0 overflow-hidden bg-white">
            <div className="flex h-[560px]">
              {/* Sidebar */}
              <div className="w-44 flex-shrink-0 border-r border-slate-200 bg-slate-50 py-4 px-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-3">Fluxo de Ganho</p>
                <div className="space-y-0.5">
                  <div className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] font-medium text-white" style={{ backgroundColor: tc500 }}>
                    <Zap className="w-4 h-4" />Pós-Ganho
                  </div>
                </div>
              </div>
              {/* Content — only Pós-Ganho actions */}
              <div className="flex-1 p-6 space-y-4 overflow-y-auto">
                {!winFlowConfig ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} /></div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><Zap className="w-4 h-4" style={{ color: tc500 }} />Ações Pós-Ganho</h3>
                    <p className="text-xs text-slate-500">Ações executadas automaticamente ao confirmar o ganho do negócio.</p>
                    {/* Email */}
                    <div className="p-3 border border-slate-200 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-blue-500" /><p className="text-sm font-medium text-slate-800">Enviar e-mail de boas-vindas</p></div>
                        <Switch checked={winFlowConfig.post_win_email_enabled || false} onCheckedChange={v => setWinFlowConfig(prev => ({ ...prev, post_win_email_enabled: v }))} />
                      </div>
                      {winFlowConfig.post_win_email_enabled && (
                        <div>
                          <Label className="text-xs text-slate-500">ID do modelo de e-mail</Label>
                          <Input value={winFlowConfig.post_win_email_template_id || ""} onChange={e => setWinFlowConfig(prev => ({ ...prev, post_win_email_template_id: e.target.value }))} placeholder="ID do template" className="mt-1" />
                        </div>
                      )}
                    </div>
                    {/* WhatsApp */}
                    <div className="p-3 border border-slate-200 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-500" /><p className="text-sm font-medium text-slate-800">Enviar WhatsApp automático</p></div>
                        <Switch checked={winFlowConfig.post_win_whatsapp_enabled || false} onCheckedChange={v => setWinFlowConfig(prev => ({ ...prev, post_win_whatsapp_enabled: v }))} />
                      </div>
                      {winFlowConfig.post_win_whatsapp_enabled && (
                        <div>
                          <Label className="text-xs text-slate-500">Mensagem</Label>
                          <textarea
                            value={winFlowConfig.post_win_whatsapp_message || ""}
                            onChange={e => setWinFlowConfig(prev => ({ ...prev, post_win_whatsapp_message: e.target.value }))}
                            placeholder="Parabéns! Sua proposta foi aprovada..."
                            className="w-full mt-1 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 h-20 resize-none focus:outline-none"
                          />
                          <p className="text-[10px] text-slate-400 mt-1">Variáveis: {"{nome}"}, {"{empresa}"}, {"{valor}"}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Save button */}
                {winFlowConfig && (
                  <Button onClick={async () => {
                    try {
                      await axios.put(`${API}/pipelines/${selectedPipeline.id}/win-flow`, winFlowConfig);
                      toast.success("Fluxo de ganho salvo!");
                    } catch { toast.error("Erro ao salvar"); }
                  }} className="w-full text-white hover:opacity-90" style={{ backgroundColor: tc500 }}>
                    <Check className="w-4 h-4 mr-2" />Salvar
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stage Fields Dialog */}
        <Dialog open={stageFieldsDialogOpen} onOpenChange={setStageFieldsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ListChecks className="w-5 h-5" style={{ color: tc500 }} />{t("ui.campos_da_etapa")}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 mb-3">Selecione os campos que devem ser preenchidos antes de sair da etapa <strong>{stageFieldsTarget?.name}</strong>. O negócio só poderá avançar para a próxima etapa se estes campos estiverem preenchidos.</p>
            <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
              {/* Native value fields based on deal type */}
              {(() => {
                const tipo = appearance.tipo_negocio_padrao || "recorrente_setup";
                const nativeFields = tipo === "recorrente_setup"
                  ? [{ field_name: "__valor_setup", label: "Setup" }, { field_name: "__valor_mrr", label: "MRR" }]
                  : [{ field_name: "__valor_negocio", label: "Valor do Negócio" }];
                return nativeFields.map(nf => {
                  const isChecked = stageFieldsList.includes(nf.field_name);
                  return (
                    <button key={nf.field_name} type="button" onClick={() => toggleStageField(nf.field_name)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 transition-colors text-left ${isChecked ? "bg-transparent" : "border-slate-200 text-slate-600"}`}
                      style={isChecked ? { borderColor: tc500 } : undefined}>
                      <div className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                        style={isChecked ? { borderColor: tc500, backgroundColor: tc500 } : { borderColor: "#cbd5e1" }}>
                        {isChecked && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium" style={isChecked ? { color: tc500 } : undefined}>{nf.label}</span>
                        <span className="text-[10px] text-slate-400 ml-2">Campo nativo</span>
                      </div>
                    </button>
                  );
                });
              })()}
              {/* Separator */}
              {allCustomFields.length > 0 && <div className="border-t border-slate-200 my-2" />}
              {/* Custom fields */}
              {allCustomFields.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nenhum campo personalizado em Negócios.</p>
              ) : allCustomFields.map(cf => {
                const isChecked = stageFieldsList.includes(cf.field_name);
                return (
                  <button key={cf.id} type="button" onClick={() => toggleStageField(cf.field_name)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 transition-colors text-left ${isChecked ? "bg-transparent" : "border-slate-200 text-slate-600"}`}
                    style={isChecked ? { borderColor: tc500 } : undefined}>
                    <div className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                      style={isChecked ? { borderColor: tc500, backgroundColor: tc500 } : { borderColor: "#cbd5e1" }}>
                      {isChecked && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium" style={isChecked ? { color: tc500 } : undefined}>{cf.field_label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <Button type="button" onClick={saveStageFields} style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90 mt-2"><Check className="w-4 h-4 mr-2" />{t("common.save")}</Button>
          </DialogContent>
        </Dialog>

        {/* Card Layout Dialog */}
        <Dialog open={cardLayoutDialogOpen} onOpenChange={(open) => { setCardLayoutDialogOpen(open); setOpenIconPicker(null); setIconSearchQuery(""); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <LayoutTemplate className="w-5 h-5" style={{ color: tc500 }} />
                Configurar Cards — {cardLayoutTarget?.name}
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-500 mb-3">Escolha quais campos aparecem nos cards desta etapa no Kanban e como eles são exibidos.</p>
            <div className="space-y-1 max-h-[52vh] overflow-y-auto pr-1">
              {cardLayoutFields.map(f => {
                const CurrentIcon = f.icon && LucideIconSet[f.icon] ? LucideIconSet[f.icon] : null;
                if (f.layout_disabled) {
                  return (
                    <div key={f.field_name} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-100 bg-slate-50">
                      <EyeOff className="w-4 h-4 text-slate-300 flex-shrink-0" />
                      <span className="flex-1 text-sm text-slate-400">{f.label || f.field_label || f.field_name}</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="flex-shrink-0 cursor-help">
                            <Info className="w-4 h-4 text-slate-300 hover:text-slate-500 transition-colors" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs text-xs">
                          Para usar este campo nos cards, vá em Configurações → Campos → Configurar Layout, selecione Negócios e marque "{f.label || f.field_label || f.field_name}" como obrigatório.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  );
                }
                return (
                  <div key={f.field_name} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${f.visible ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50"}`}>
                    <button type="button" onClick={() => { if (f.field_name !== "titulo") updateCardField(f.field_name, "visible", !f.visible); }}
                      className={`flex-shrink-0 ${f.visible ? "" : "opacity-40"} ${f.field_name === "titulo" ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      {f.visible ? <Eye className="w-4 h-4" style={{ color: tc500 }} /> : <EyeOff className="w-4 h-4 text-slate-300" />}
                    </button>
                    <span className={`flex-1 text-sm ${f.visible ? "text-slate-800 font-medium" : "text-slate-400"}`}>{f.label || f.field_label || f.field_name}</span>
                    {f.visible && (
                      <>
                        <button type="button" onClick={() => updateCardField(f.field_name, "bold", !f.bold)}
                          className={`flex-shrink-0 w-7 h-7 rounded flex items-center justify-center border transition-colors ${f.bold ? "border-transparent text-white" : "border-slate-200 text-slate-400 bg-white hover:bg-slate-100"}`}
                          style={f.bold ? { backgroundColor: tc500 } : undefined} title="Negrito"><Bold className="w-3.5 h-3.5" /></button>
                        <button type="button" onClick={() => updateCardField(f.field_name, "badge", !f.badge)}
                          className={`flex-shrink-0 w-7 h-7 rounded flex items-center justify-center border transition-colors ${f.badge ? "border-transparent text-white" : "border-slate-200 text-slate-400 bg-white hover:bg-slate-100"}`}
                          style={f.badge ? { backgroundColor: tc500 } : undefined} title="Badge"><Tag className="w-3.5 h-3.5" /></button>
                        {f.badge && (
                          <label className="flex-shrink-0 w-7 h-7 rounded cursor-pointer overflow-hidden border border-slate-200" title="Cor">
                            <input type="color" value={f.badge_color || "#3B82F6"} onChange={e => updateCardField(f.field_name, "badge_color", e.target.value)} className="w-9 h-9 -m-1 cursor-pointer border-0 p-0" />
                          </label>
                        )}
                        {/* Icon Picker via Popover */}
                        <Popover open={openIconPicker === f.field_name} onOpenChange={(open) => { setOpenIconPicker(open ? f.field_name : null); if (!open) setIconSearchQuery(""); }}>
                          <PopoverTrigger asChild>
                            <button type="button"
                              className={`flex-shrink-0 w-7 h-7 rounded border-2 flex items-center justify-center transition-colors ${openIconPicker === f.field_name || f.icon ? "border-transparent text-white" : "border-slate-200 text-slate-300 bg-white hover:border-slate-400"}`}
                              style={(openIconPicker === f.field_name || f.icon) ? { backgroundColor: tc500 } : undefined} title="Ícone">
                              {CurrentIcon ? <CurrentIcon className="w-3.5 h-3.5 text-white" /> : <Smile className="w-3.5 h-3.5" />}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" side="left" className="w-80 p-2">
                            <input autoFocus type="text" placeholder="Buscar ícone... (ex: home, star, user)" value={iconSearchQuery}
                              onChange={e => setIconSearchQuery(e.target.value)}
                              className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 mb-2 outline-none focus:border-slate-400" />
                            {iconSearchQuery.length < 2 ? (
                              <div className="grid grid-cols-9 gap-0.5 max-h-52 overflow-y-auto overflow-x-hidden">
                                {CARD_ICON_OPTIONS.slice(0, 81).map(opt => (
                                  <button key={opt.name} type="button"
                                    onClick={() => { updateCardField(f.field_name, "icon", opt.name === "none" ? null : opt.name); setOpenIconPicker(null); setIconSearchQuery(""); }}
                                    className={`w-7 h-7 flex items-center justify-center rounded transition-colors hover:bg-slate-100 ${f.icon === opt.name ? "bg-slate-200 ring-1 ring-slate-400" : ""}`}
                                    title={opt.label}>
                                    {opt.Icon ? <opt.Icon className="w-4 h-4 text-slate-600" /> : <ImageOff className="w-3.5 h-3.5 text-slate-300" />}
                                  </button>
                                ))}
                                <p className="col-span-9 text-center text-[10px] text-slate-400 pt-1">Digite para buscar entre {CARD_ICON_OPTIONS.length - 1} ícones</p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-9 gap-0.5 max-h-52 overflow-y-auto overflow-x-hidden">
                                {CARD_ICON_OPTIONS.filter(opt =>
                                  opt.label.toLowerCase().includes(iconSearchQuery.toLowerCase()) || opt.name.toLowerCase().includes(iconSearchQuery.toLowerCase())
                                ).slice(0, 200).map(opt => (
                                  <button key={opt.name} type="button"
                                    onClick={() => { updateCardField(f.field_name, "icon", opt.name === "none" ? null : opt.name); setOpenIconPicker(null); setIconSearchQuery(""); }}
                                    className={`w-7 h-7 flex items-center justify-center rounded transition-colors hover:bg-slate-100 ${f.icon === opt.name ? "bg-slate-200 ring-1 ring-slate-400" : ""}`}
                                    title={opt.label}>
                                    {opt.Icon ? <opt.Icon className="w-4 h-4 text-slate-600" /> : <ImageOff className="w-3.5 h-3.5 text-slate-300" />}
                                  </button>
                                ))}
                              </div>
                            )}
                          </PopoverContent>
                        </Popover>
                      </>
                    )}
                  </div>
                );
              })}
              {cardLayoutFields.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Nenhum campo no layout do módulo Negócios.</p>}
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <Button type="button" variant="outline" onClick={() => setApplyToAllOpen(true)} className="w-full text-slate-600 border-slate-200">
                <Copy className="w-4 h-4 mr-2" />{t("ui.aplicar_a_todas_as_etapas")}</Button>
              <Button type="button" onClick={saveCardLayout} style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90">
                <Check className="w-4 h-4 mr-2" />{t("common.save")} Configuração
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Apply to All Stages Confirmation */}
        <AlertDialog open={applyToAllOpen} onOpenChange={setApplyToAllOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading flex items-center gap-2"><Copy className="w-5 h-5" style={{ color: tc500 }} />{t("ui.aplicar_a_todas_as_etapas")}</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-500">
                Deseja aplicar estas configurações de cards para <strong>todas as {stages.length} etapas</strong> do pipeline <strong>{selectedPipeline?.name}</strong>? Esta ação substituirá as configurações existentes de cada etapa.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="text-slate-600"><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={applyToAllStages} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Check className="w-4 h-4 mr-2" />{t("ui.aplicar_a_todas")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Stage Dialog */}
        <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><GitBranch className="w-5 h-5" style={{ color: tc500 }} />{editStage ? "Editar" : "Nova"} Etapa</DialogTitle></DialogHeader>
            <form onSubmit={submitStage} className="space-y-4">
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Informações da etapa</p>
                <div className="border border-slate-200 rounded-lg px-4 py-4 space-y-3">
                  <div><Label className="text-slate-700">{t("common.name")}</Label><Input value={stageForm.name} onChange={e => setStageForm({...stageForm, name: e.target.value})} required placeholder="Ex: Qualificação, Proposta..." /></div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Cor</Label>
                    <div className="space-y-1.5">
                      {COLOR_SCALES.map(scale => (
                        <div key={scale.label} className="flex gap-1">
                          {scale.colors.map(c => (
                            <button key={c} type="button" onClick={() => setStageForm({...stageForm, color: c})}
                              className={`w-7 h-7 rounded-md transition-all ${stageForm.color === c ? "ring-2 ring-offset-1 ring-slate-900 scale-110" : "hover:scale-105"}`}
                              style={{ backgroundColor: c }} />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <Button type="submit" style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90"><Check className="w-4 h-4 mr-2" />{t("common.save")}</Button>
            </form>
          </DialogContent>
        </Dialog>

        <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={() => { setDeleteTarget(null); setDeleteType(""); }} title={`Excluir ${deleteType === "pipeline" ? "pipeline" : "etapa"}`} description={`${t("common.confirm_delete")} "${deleteTarget?.name}"?`} onConfirm={confirmDelete} />

        {/* Duplicate Stage Confirmation */}
        <AlertDialog open={!!duplicateStageTarget} onOpenChange={() => setDuplicateStageTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading flex items-center gap-2"><Copy className="w-5 h-5" style={{ color: tc500 }} />{t("ui.duplicar_etapa")}</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-500">
                Deseja duplicar a etapa <strong>"{duplicateStageTarget?.name}"</strong>? Todas as suas configurações (campos obrigatórios, layout de cards) serão copiadas para a nova etapa.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="text-slate-600"><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDuplicateStage} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Copy className="w-4 h-4 mr-2" />{t("common.duplicate")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Stage SLA Dialog */}
        <Dialog open={slaDialogOpen} onOpenChange={setSlaDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Timer className="w-5 h-5" style={{ color: tc500 }} />
                Prazo da Etapa — {slaTarget?.name}
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-500 mb-4">Defina prazos opcionais. Ao excedê-los, o card ficará com borda vermelha no Kanban.</p>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700 mb-1 block flex items-center gap-1">
                  <Timer className="w-3 h-3" />Prazo máximo na etapa (dias)
                </Label>
                <Input type="number" min="1" placeholder="Ex: 7 (deixe vazio para sem limite)"
                  value={slaForm.prazo_dias}
                  onChange={e => setSlaForm({...slaForm, prazo_dias: e.target.value})} />
                <p className="text-[11px] text-slate-400 mt-1">Tempo máximo que um card pode permanecer nesta etapa.</p>
              </div>
              <div>
                <Label className="text-slate-700 mb-1 block flex items-center gap-1">
                  <Timer className="w-3 h-3" />Prazo sem movimentação (dias)
                </Label>
                <Input type="number" min="1" placeholder="Ex: 3 (deixe vazio para sem limite)"
                  value={slaForm.prazo_inatividade_dias}
                  onChange={e => setSlaForm({...slaForm, prazo_inatividade_dias: e.target.value})} />
                <p className="text-[11px] text-slate-400 mt-1">Tempo sem edição, movimentação ou comentário.</p>
              </div>
            </div>
            <Button type="button" onClick={saveSla} style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90 mt-2">
              <Check className="w-4 h-4 mr-2" />{t("common.save")} Prazo
            </Button>
          </DialogContent>
        </Dialog>

        {/* Reorder Confirm */}
        <AlertDialog open={!!reorderPending} onOpenChange={() => setReorderPending(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading flex items-center gap-2"><GripVertical className="w-5 h-5" style={{ color: tc500 }} />Reordenar Etapa</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-500">
                Deseja mover <strong>{reorderPending?.stageName}</strong> para a etapa número <strong>{reorderPending?.newPosition}</strong>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={cancelReorder} className="text-slate-600"><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmReorder} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Check className="w-4 h-4 mr-2" />{t("common.confirm")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle className="font-heading flex items-center gap-2"><Download className="w-5 h-5" style={{ color: tc500 }} />Exportar Etapas</AlertDialogTitle><AlertDialogDescription className="text-slate-500">Deseja fazer download de todas as informações da listagem em formato de planilha?</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="text-slate-600"><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel><AlertDialogAction onClick={handleExport} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Download className="w-4 h-4 mr-2" />{t("ui.baixar_excel")}</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // =================== PIPELINE LIST VIEW ===================
  return (
    <div data-testid="pipeline-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}><GitBranch className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} /></div>
          <div><h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">{t("configuracoes.pipeline.title")}<Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{filteredPipelines.length}</Badge></h1><p className="text-slate-500 mt-0.5">{t("configuracoes.pipeline.subtitle")}</p></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar pipeline..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
          </div>
          <Tooltip><TooltipTrigger asChild><Button onClick={openNewPipeline} size="icon" style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90 h-9 w-9"><Plus className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("configuracoes.pipeline.new")}</TooltipContent></Tooltip>
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          [...Array(2)].map((_, i) => <div key={i} className="h-20 bg-white rounded-lg border border-slate-200 animate-pulse" />)
        ) : filteredPipelines.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-white rounded-lg border border-slate-200">
            <GitBranch className="w-10 h-10 mb-3 text-slate-300" /><p className="text-sm">{pipelines.length === 0 ? "Nenhum pipeline criado." : "Nenhum pipeline encontrado."}</p>
          </div>
        ) : filteredPipelines.map(p => (
          <div key={p.id} data-testid={`pipeline-card-${p.id}`}
            className="bg-white rounded-lg border border-slate-200 shadow-sm hover:border-slate-400 transition-colors p-4 flex items-center gap-4 cursor-pointer"
            onClick={() => { setSelectedPipeline(p); setSearchTerm(""); }}>
            <div style={{ backgroundColor: tc500 }} className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"><span className="text-white font-semibold text-lg">{p.name?.charAt(0).toUpperCase()}</span></div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-slate-900">{p.name}</span>
              <div className="flex items-center gap-2 mt-0.5"><div className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-300 bg-white text-slate-500 text-[11px]"><Layers className="w-2.5 h-2.5" />{p.stages_count || 0} etapas</div></div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); setDuplicatePipelineTarget(p); }} className="text-slate-500 hover:text-violet-600 hover:bg-violet-50"><Copy className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("common.duplicate")}</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); openEditPipeline(p); }} className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"><Pencil className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("common.edit")}</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={ev => { ev.stopPropagation(); setDeleteTarget(p); setDeleteType("pipeline"); }} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{t("common.delete")}</TooltipContent></Tooltip>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={pipelineDialogOpen} onOpenChange={setPipelineDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><GitBranch className="w-5 h-5" style={{ color: tc500 }} />{editPipeline ? "Editar" : "Novo"} Pipeline</DialogTitle></DialogHeader>
          <form onSubmit={submitPipeline} className="space-y-4">
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Informações do pipeline</p>
              <div className="border border-slate-200 rounded-lg px-4 py-4 space-y-3"><div><Label className="text-slate-700">{t("common.name")}</Label><Input value={pipelineForm.name} onChange={e => setPipelineForm({...pipelineForm, name: e.target.value})} required placeholder="Ex: Vendas Inbound, Onboarding..." /></div></div>
            </div>
            <Button type="submit" style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90"><Check className="w-4 h-4 mr-2" />{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={() => { setDeleteTarget(null); setDeleteType(""); }} title="Excluir pipeline" description={`${t("common.confirm_delete")} o pipeline "${deleteTarget?.name}"?`} onConfirm={confirmDelete} />

      {/* Duplicate Pipeline Confirmation */}
      <AlertDialog open={!!duplicatePipelineTarget} onOpenChange={() => setDuplicatePipelineTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Copy className="w-5 h-5" style={{ color: tc500 }} />{t("ui.duplicar_pipeline")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Deseja duplicar o pipeline <strong>"{duplicatePipelineTarget?.name}"</strong>? Todas as etapas, configurações, motivos de perda e layouts de cards serão copiados para o novo pipeline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-slate-600"><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDuplicatePipeline} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Copy className="w-4 h-4 mr-2" />{t("common.duplicate")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
